import {
  Briefcase,
  CalendarDays,
  Clock,
  FolderKanban,
  LayoutDashboard,
  ListTodo,
  Package,
  Users,
} from 'lucide-react';

export interface NavSubItem {
  title: string;
  href: string;
}

export interface NavItem {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavSubItem[];
  matchPaths?: string[];
}

/** Everything the sidebar needs to decide what a given user may navigate to. */
export interface NavAccess {
  /** `home-dashboard` extension row is `status = 'enabled'`. */
  isDashboardEnabled: boolean;
  /** `task-manager` extension row is `status = 'enabled'`. */
  isTaskManagerEnabled: boolean;
  /** `time-tracker` extension row is `status = 'enabled'`. */
  isTimeTrackerEnabled: boolean;
  /** `boards.view` — the one permission every /boards endpoint requires. */
  canViewBoards: boolean;
  /** `time.view` — the permission GET /time-entries requires. */
  canViewTime: boolean;
  canViewVacancies: boolean;
  canViewCandidates: boolean;
  canViewBalances: boolean;
  /** `assets.view` — what `/inventory`'s route gate and `GET /assets/inventory` require. */
  canViewInventory: boolean;
}

const baseNavItems: NavItem[] = [
  { title: 'People', href: '/people', icon: Users, matchPaths: ['/people', '/employees'] },
  { title: 'Inventory', href: '/inventory', icon: Package, matchPaths: ['/inventory'] },
  { title: 'Projects', href: '/projects', icon: FolderKanban },
  {
    title: 'Recruitment',
    icon: Briefcase,
    children: [
      { title: 'Vacancies', href: '/recruitment/vacancies' },
      { title: 'Candidates', href: '/recruitment/candidates' },
    ],
  },
  {
    title: 'Absences',
    icon: CalendarDays,
    children: [
      { title: 'Calendar', href: '/absences/calendar' },
      { title: 'Requests', href: '/absences/request' },
    ],
  },
];

/**
 * The sidebar for one user.
 *
 * Two independent gates apply to the extension entries, and both must pass:
 * the extension has to be installed and enabled (an org-level switch), and the
 * user has to hold the permission the pages behind it enforce. Showing an entry
 * on the extension flag alone hands every tester a link that dead-ends in a 403,
 * which reads as a broken release rather than as a missing grant — the same
 * reason Recruitment and Balances are permission-filtered below.
 */
export function buildMainNavItems(access: NavAccess): NavItem[] {
  const items = [...baseNavItems];

  // The dashboard is an org-level switch, not a permission: while the
  // `home-dashboard` extension is off, `/` lands on People and the entry is
  // simply absent — the ship-dark lever the AI assistant already uses. No
  // permission gate on purpose: widget access is decided by role assignment,
  // and an empty dashboard explains itself.
  if (access.isDashboardEnabled) {
    items.unshift({
      title: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      matchPaths: ['/dashboard'],
    });
  }

  if (access.isTaskManagerEnabled && access.canViewBoards) {
    const projectsIdx = items.findIndex((i) => i.href === '/projects');
    items.splice(projectsIdx + 1, 0, {
      title: 'Boards',
      href: '/boards',
      icon: ListTodo,
    });
  }
  if (access.isTimeTrackerEnabled && access.canViewTime) {
    items.push({
      title: 'Time Tracker',
      href: '/time',
      icon: Clock,
    });
  }

  // Filter Recruitment children by per-module view permissions; drop the
  // parent entirely if the user can't see either Vacancies or Candidates.
  const recruitmentIdx = items.findIndex((i) => i.title === 'Recruitment');
  if (recruitmentIdx !== -1) {
    const recruitment = items[recruitmentIdx];
    const allowedChildren = (recruitment.children ?? []).filter((child) => {
      if (child.href === '/recruitment/vacancies') return access.canViewVacancies;
      if (child.href === '/recruitment/candidates') return access.canViewCandidates;
      return true;
    });
    if (allowedChildren.length === 0) {
      items.splice(recruitmentIdx, 1);
    } else {
      items[recruitmentIdx] = { ...recruitment, children: allowedChildren };
    }
  }

  // Inventory's route already bounces a viewer without `assets.view` back to
  // People (HW-583); listing it for them is a link that goes nowhere. [HW-940]
  if (!access.canViewInventory) {
    const inventoryIdx = items.findIndex((i) => i.href === '/inventory');
    if (inventoryIdx !== -1) items.splice(inventoryIdx, 1);
  }

  // Balances child is HR-only (absences.balances.view).
  const absencesIdx = items.findIndex((i) => i.title === 'Absences');
  if (absencesIdx !== -1 && access.canViewBalances) {
    const absences = items[absencesIdx];
    items[absencesIdx] = {
      ...absences,
      children: [...(absences.children ?? []), { title: 'Balances', href: '/absences/balances' }],
    };
  }

  return items;
}
